import * as signalr from "@microsoft/signalr";

// SignalR's own reconnect covers short blips; once it gives up, keepConnected takes over so a
// long outage (a deploy, a sleeping laptop) never leaves the page deaf for good.
const automaticRetryDelays = [0, 2_000, 10_000, 30_000];
const restartDelays = [5_000, 10_000, 30_000];
const maxRestartDelay = 60_000;

export function buildHubConnection(url: string): signalr.HubConnection {
    return new signalr.HubConnectionBuilder()
        .withUrl(url)
        .withAutomaticReconnect(automaticRetryDelays)
        .build();
}

type RestartableConnection = Pick<
    signalr.HubConnection,
    "state" | "start" | "stop" | "onclose" | "onreconnected"
>;

export type KeptConnection = {
    start(): Promise<void>;
    stop(): Promise<void>;
};

/**
 * Keeps a hub connected until stop(): retries a failed first start, restarts after SignalR gives
 * up, and retries at once when the tab is shown again or the network returns. onRestored runs
 * after every reconnect, since anything pushed while disconnected was missed.
 */
export function keepConnected(
    connection: RestartableConnection,
    onRestored: () => void
): KeptConnection {
    let stopped = false;
    let connectedBefore = false;
    let attempts = 0;
    let timer: number | undefined;

    function clearTimer() {
        window.clearTimeout(timer);
        timer = undefined;
    }

    async function attempt() {
        clearTimer();
        if (stopped || connection.state !== signalr.HubConnectionState.Disconnected) return;
        try {
            await connection.start();
            attempts = 0;
            if (connectedBefore) onRestored();
            connectedBefore = true;
        } catch {
            schedule();
        }
    }

    function schedule() {
        if (stopped || timer !== undefined) return;
        const delay = restartDelays[attempts] ?? maxRestartDelay;
        attempts++;
        timer = window.setTimeout(() => void attempt(), delay);
    }

    function retryNow() {
        if (document.visibilityState !== "visible") return;
        attempts = 0;
        void attempt();
    }

    connection.onreconnected(() => onRestored());
    connection.onclose(() => schedule());
    document.addEventListener("visibilitychange", retryNow);
    window.addEventListener("online", retryNow);

    return {
        start: attempt,
        async stop() {
            stopped = true;
            clearTimer();
            document.removeEventListener("visibilitychange", retryNow);
            window.removeEventListener("online", retryNow);
            try {
                await connection.stop();
            } catch {
                // Already down; nothing to close.
            }
        },
    };
}
